import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  Image as ImageIcon,
  LayoutGrid,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { handleCategoryCreate } from "../../requests/Categories/CategoryService";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const emptySubcategory = (index) => ({
  id: `sub-${Date.now()}-${index}`,
  name: "",
  cover: null,
  coverPreview: "",
  thumbnail: null,
  thumbnailPreview: "",
  active: true,
  featured: false,
  open: true,
});

const readImage = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      reject(new Error("Use a JPG or PNG image."));
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      reject(new Error("Image must be 5MB or smaller."));
      return;
    }
    const preview = URL.createObjectURL(file);
    resolve({ file, preview });
  });

function UploadTile({ title, caption, preview, onFile }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold text-gray-800">{title}</p>
      <label className="relative flex h-36 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed border-gray-300 bg-gradient-to-b from-slate-100 to-slate-300/70">
        {preview ? (
          <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center text-slate-400">
            <ImageIcon size={22} strokeWidth={1.5} />
            <span className="mt-2 text-[10px] font-semibold tracking-[0.16em]">{caption}</span>
          </span>
        )}
        <span className="absolute bottom-3 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-800 shadow-sm">
          <Upload size={12} />
          Upload
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png"
          className="sr-only"
          onChange={onFile}
        />
      </label>
      <p className="mt-1.5 text-[11px] text-gray-400">JPG or PNG · 5MB max</p>
    </div>
  );
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors focus:outline-none focus-visible:outline-none ${
        checked ? "bg-[#FF6C6C]" : "bg-gray-300"
      }`}
    >
      <span
        className={`h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

const fieldClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none ring-0 focus:border-gray-300 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0";

const CreateCategoryModal = ({ isOpen, onClose, onSave, parentCategory = null }) => {
  const placement = parentCategory?.id ? "subcategory" : "parent";
  const parentId = parentCategory?.id ? String(parentCategory.id) : "";
  const parentName = parentCategory?.name || parentCategory?.categoryName || "";
  const [name, setName] = useState("");
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [active, setActive] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [bulkNames, setBulkNames] = useState("");
  const [nameError, setNameError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null);
  const scrollRef = useRef(null);
  const focusSubcategoryId = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setName("");
    setCover(null);
    setCoverPreview("");
    setThumbnail(null);
    setThumbnailPreview("");
    setActive(true);
    setFeatured(false);
    setNameError("");
    setSubmitting(false);
    setSaveProgress(null);
    setBulkNames("");
    if (parentCategory?.id) {
      const first = emptySubcategory(1);
      focusSubcategoryId.current = first.id;
      setSubcategories([first]);
    } else {
      setSubcategories([]);
    }
    return undefined;
  }, [isOpen, parentCategory]);

  useEffect(() => {
    if (!saveProgress?.id || !scrollRef.current) return undefined;
    const frame = requestAnimationFrame(() => {
      const container = scrollRef.current;
      const card = container?.querySelector(`[data-saving-card="${saveProgress.id}"]`);
      if (!container || !card) return;
      const padding = 16;
      const containerRect = container.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const cardTop = container.scrollTop + (cardRect.top - containerRect.top);
      const available = container.clientHeight;
      const nextTop =
        cardRect.height + padding * 2 <= available
          ? cardTop - (available - cardRect.height) / 2
          : cardTop - padding;
      const maxTop = Math.max(0, container.scrollHeight - available);
      container.scrollTo({
        top: Math.min(Math.max(0, nextTop), maxTop),
        behavior: "auto",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [saveProgress]);

  useEffect(() => {
    const id = focusSubcategoryId.current;
    if (!id) return undefined;
    focusSubcategoryId.current = null;
    const frame = requestAnimationFrame(() => {
      const input = scrollRef.current?.querySelector(`[data-subcategory-id="${id}"]`);
      if (!input) return;
      input.focus({ preventScroll: true });
      input.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [subcategories]);

  const displayName = name.trim() || "Category name";
  const eyebrow = placement === "parent" ? "Parent Category" : "Subcategory";

  const onPick = async (event, apply) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const image = await readImage(file);
      apply(image);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const addSubcategory = () => {
    const next = emptySubcategory(subcategories.length + 1);
    focusSubcategoryId.current = next.id;
    setSubcategories((current) => [...current, next]);
  };

  const applyBulkNames = () => {
    const names = bulkNames
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (!names.length) {
      setNameError("Enter subcategory names separated by commas.");
      return;
    }
    setNameError("");
    setSubcategories((current) => {
      const kept = current.filter((item) => item.name.trim() || item.cover || item.thumbnail);
      const next = names.map((entry, index) => ({
        ...emptySubcategory(kept.length + index + 1),
        name: entry,
        open: true,
      }));
      focusSubcategoryId.current = next[0]?.id || null;
      return [...kept, ...next];
    });
    setBulkNames("");
  };

  const updateSubcategory = (id, patch) => {
    setSubcategories((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  };

  const removeSubcategory = (id) => {
    setSubcategories((current) => {
      const target = current.find((item) => item.id === id);
      if (target?.coverPreview) URL.revokeObjectURL(target.coverPreview);
      if (target?.thumbnailPreview) URL.revokeObjectURL(target.thumbnailPreview);
      return current.filter((item) => item.id !== id);
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    const pending = subcategories.filter((item) => item.name.trim() || item.cover || item.thumbnail);
    if (placement === "subcategory") {
      if (!parentId) {
        setNameError("Choose the parent category these subcategories sit under.");
        return;
      }
      if (!pending.length) {
        setNameError("Add at least one subcategory.");
        return;
      }
      if (pending.some((item) => item.name.trim().length < 3)) {
        setNameError("Each subcategory name must be at least 3 characters.");
        return;
      }
      setNameError("");
      setSubmitting(true);
      setSaveProgress(null);
      await handleCategoryCreate(
        {
          placement,
          parentId,
          subcategories: pending,
        },
        { onSave, onClose, onProgress: setSaveProgress }
      );
      setSubmitting(false);
      setSaveProgress(null);
      return;
    }
    if (name.trim().length < 3) {
      setNameError("Category name must be at least 3 characters.");
      return;
    }
    if (pending.some((item) => item.name.trim().length < 3)) {
      setNameError("Each subcategory name must be at least 3 characters.");
      return;
    }
    setNameError("");
    setSubmitting(true);
    setSaveProgress(null);
    await handleCategoryCreate(
      {
        placement,
        parentId,
        name,
        cover,
        thumbnail,
        active,
        featured,
        subcategories: pending,
      },
      { onSave, onClose, onProgress: setSaveProgress }
    );
    setSubmitting(false);
    setSaveProgress(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="category-drawer-backdrop"
          className="fixed inset-0 z-[1000] bg-black/45"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
      )}
      {isOpen && (
          <motion.form
            key="category-drawer"
            onSubmit={submit}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="category-drawer fixed inset-y-0 right-0 z-[1001] flex h-full w-full max-w-[440px] flex-col overflow-hidden bg-white shadow-2xl"
          >
            <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3 pt-4">
              <div className="flex items-start gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FFF1F0] text-[#FF6C6C]">
                  <LayoutGrid size={16} />
                </span>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">
                    {placement === "parent" ? "New category" : "Add subcategories"}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {placement === "parent"
                      ? "A top-level shop section"
                      : `Under ${parentName}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            </div>

            <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 pb-4">
              {placement === "parent" && (
              <>
              <div className="rounded-2xl border border-gray-200 bg-white p-3">
                <div className="relative">
                  <div className="flex h-32 items-center justify-center overflow-hidden rounded-xl bg-slate-100">
                    {coverPreview ? (
                      <img src={coverPreview} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon size={22} className="text-slate-300" />
                    )}
                  </div>
                  <div className="absolute -bottom-4 left-3 flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border-4 border-white bg-slate-100 shadow-sm">
                    {thumbnailPreview ? (
                      <img src={thumbnailPreview} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon size={14} className="text-slate-300" />
                    )}
                  </div>
                </div>
                <p className="mt-6 text-[10px] font-semibold tracking-[0.16em] text-[#FF6C6C]">
                  {eyebrow}
                </p>
                <p className="text-sm font-semibold text-gray-900">{displayName}</p>
              </div>

              <section className="rounded-2xl border border-gray-200 p-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-800">Name</span>
                  <input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={placement === "parent" ? "Home & Kitchen" : "Subcategory name"}
                    className={fieldClass}
                  />
                </label>
              </section>

              <section className="rounded-2xl border border-gray-200 p-3">
                <p className="text-xs font-semibold text-gray-800">Images</p>
                <p className="mb-3 text-[11px] text-gray-400">
                  Cover shows on the storefront. Thumbnail shows in lists.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <UploadTile
                    title="Cover image"
                    caption="CATEGORY COVER"
                    preview={coverPreview}
                    onFile={(event) =>
                      onPick(event, (image) => {
                        if (coverPreview) URL.revokeObjectURL(coverPreview);
                        setCover(image.file);
                        setCoverPreview(image.preview);
                      })
                    }
                  />
                  <UploadTile
                    title="Thumbnail"
                    caption="LIST THUMBNAIL"
                    preview={thumbnailPreview}
                    onFile={(event) =>
                      onPick(event, (image) => {
                        if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
                        setThumbnail(image.file);
                        setThumbnailPreview(image.preview);
                      })
                    }
                  />
                </div>
              </section>

              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 px-3 py-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900">Active</p>
                    <p className="text-[11px] leading-tight text-gray-400">Visible to shoppers</p>
                  </div>
                  <Switch checked={active} onChange={setActive} label="Active" />
                </div>
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 px-3 py-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900">Featured</p>
                    <p className="text-[11px] leading-tight text-gray-400">Highlight on storefront</p>
                  </div>
                  <Switch checked={featured} onChange={setFeatured} label="Featured" />
                </div>
              </div>

              </>
              )}

              {placement === "subcategory" && (
                <section className="rounded-2xl border border-gray-200 p-3">
                  <p className="text-xs font-semibold text-gray-800">Sits under</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{parentName}</p>
                </section>
              )}

              <section className="rounded-2xl border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-900">Subcategories</p>
                      <p className="text-[11px] text-gray-400">
                        {placement === "parent"
                          ? "Add subcategories now. Each one is created after the parent category."
                          : `Add every subcategory for ${parentName}. Each one is created on its own, in order.`}
                      </p>
                    </div>
                    {subcategories.length > 0 && (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600">
                        {subcategories.length}
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex gap-2">
                    <input
                      value={bulkNames}
                      onChange={(event) => setBulkNames(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          applyBulkNames();
                        }
                      }}
                      placeholder="Home Audio, Speakers, Headphones"
                      className={fieldClass}
                    />
                    <button
                      type="button"
                      onClick={applyBulkNames}
                      className="shrink-0 rounded-xl bg-[#FFF1F0] px-3 text-xs font-semibold text-[#FF6C6C] hover:bg-[#FFE4E2]"
                    >
                      Apply
                    </button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-gray-400">
                    Separate names with commas, then apply to open a form for each one.
                  </p>

                  <div className="mt-3 space-y-2">
                    {subcategories.map((item, index) => (
                      <div
                        key={item.id}
                        data-saving-card={item.id}
                        className={`rounded-2xl border p-2.5 ${
                          saveProgress?.id === item.id
                            ? "border-[#FF6C6C] bg-[#FFF8F7]"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-100 text-[11px] font-semibold text-gray-500">
                            {saveProgress?.id === item.id ? (
                              <Loader2 size={13} className="animate-spin text-[#FF6C6C]" />
                            ) : (
                              index + 1
                            )}
                          </span>
                          <input
                            data-subcategory-id={item.id}
                            value={item.name}
                            onChange={(event) => updateSubcategory(item.id, { name: event.target.value })}
                            placeholder={`Subcategory ${index + 1} name`}
                            className="min-w-0 flex-1 border-0 bg-transparent text-sm font-medium text-gray-900 shadow-none outline-none ring-0 placeholder:font-normal placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                          />
                          <button
                            type="button"
                            aria-label={item.open ? "Collapse subcategory" : "Expand subcategory"}
                            onClick={() => updateSubcategory(item.id, { open: !item.open })}
                            className="rounded-md p-1 text-gray-400 hover:bg-gray-100"
                          >
                            <ChevronDown size={15} className={item.open ? "" : "-rotate-90"} />
                          </button>
                          <button
                            type="button"
                            aria-label="Remove subcategory"
                            onClick={() => removeSubcategory(item.id)}
                            className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        {item.open && (
                          <div className="mt-3 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <UploadTile
                                title="Cover image"
                                caption="COVER"
                                preview={item.coverPreview}
                                onFile={(event) =>
                                  onPick(event, (image) => {
                                    if (item.coverPreview) URL.revokeObjectURL(item.coverPreview);
                                    updateSubcategory(item.id, {
                                      cover: image.file,
                                      coverPreview: image.preview,
                                    });
                                  })
                                }
                              />
                              <UploadTile
                                title="Thumbnail"
                                caption="THUMBNAIL"
                                preview={item.thumbnailPreview}
                                onFile={(event) =>
                                  onPick(event, (image) => {
                                    if (item.thumbnailPreview) URL.revokeObjectURL(item.thumbnailPreview);
                                    updateSubcategory(item.id, {
                                      thumbnail: image.file,
                                      thumbnailPreview: image.preview,
                                    });
                                  })
                                }
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 px-2.5 py-2.5">
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-gray-900">Active</p>
                                  <p className="text-[11px] leading-tight text-gray-400">Visible to shoppers</p>
                                </div>
                                <Switch
                                  checked={item.active}
                                  onChange={(value) => updateSubcategory(item.id, { active: value })}
                                  label="Subcategory active"
                                />
                              </div>
                              <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 px-2.5 py-2.5">
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-gray-900">Featured</p>
                                  <p className="text-[11px] leading-tight text-gray-400">Highlight on storefront</p>
                                </div>
                                <Switch
                                  checked={item.featured}
                                  onChange={(value) => updateSubcategory(item.id, { featured: value })}
                                  label="Subcategory featured"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addSubcategory}
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-gray-300 py-2.5 text-xs font-semibold text-gray-600 hover:border-[#FF6C6C] hover:text-[#FF6C6C]"
                  >
                    <Plus size={14} />
                    {subcategories.length ? "Add another subcategory" : "Add a subcategory"}
                  </button>
                </section>

              {nameError && <p className="text-xs text-[#E25555]">{nameError}</p>}
            </div>

            {saveProgress && (
              <div className="flex items-center gap-2.5 border-t border-gray-100 px-4 py-2.5">
                <Loader2 size={16} className="shrink-0 animate-spin text-[#FF6C6C]" />
                <p className="min-w-0 text-xs text-gray-700">
                  <span className="font-semibold">Saving {saveProgress.name}</span>
                  <span className="text-gray-400">
                    {" "}
                    · {saveProgress.index} of {saveProgress.total}
                  </span>
                </p>
              </div>
            )}

            <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-xl bg-[#FF6C6C] py-2.5 text-sm font-semibold text-white hover:bg-[#ff5252] disabled:opacity-70"
              >
                {saveProgress
                  ? `Saving ${saveProgress.index} of ${saveProgress.total}`
                  : submitting
                    ? "Creating..."
                    : placement === "parent"
                      ? "Create category"
                      : subcategories.filter((item) => item.name.trim()).length > 1
                        ? "Create subcategories"
                        : "Create subcategory"}
              </button>
            </div>
          </motion.form>
      )}
    </AnimatePresence>
  );
};

export default CreateCategoryModal;
